
import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  CircularProgress
} from '@mui/material';
import { usePinContext } from '../contexts/PinContext';
import { useTranslation } from 'react-i18next';
import { getUser, isDemo } from '../services/auth';

interface PinInputDialogProps {
  open: boolean;
  onClose: () => void;
  onPinVerified: () => void;
  deviceId?: number;
  embedded?: boolean; // Si está embebido en otro componente (sin backdrop)
  onShowRestriction?: () => void; // Callback para mostrar snackbar de restricción
}

const PinInputDialog: React.FC<PinInputDialogProps> = ({
  open,
  onClose,
  onPinVerified,
  deviceId,
  embedded = false,
  onShowRestriction
}) => {
  const { t } = useTranslation();
  const [pin, setPin] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [error, setError] = useState('');
  const [countdown, setCountdown] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const previousOpenRef = useRef(false);
  
  const {
    isBlocked,
    getBlockTimeRemaining,
    addPinAttempt,
    setPinSession
  } = usePinContext();

  // Actualizar countdown cada segundo si está bloqueado
  useEffect(() => {
    if (open && isBlocked()) {
      const interval = setInterval(() => {
        const remaining = getBlockTimeRemaining();
        setCountdown(remaining);
        
        if (remaining === 0) {
          clearInterval(interval);
        }
      }, 1000);

      return () => clearInterval(interval);
    }
  }, [open, isBlocked, getBlockTimeRemaining]);

  // Verificar si el usuario es demo
  const user = getUser();
  const isUserDemo = user ? isDemo(user) : false;

  // Resetear estado SOLO cuando el diálogo se abre (transición false -> true)
  useEffect(() => {
    if (open && !previousOpenRef.current) {
      // El diálogo acaba de abrirse
      setPin('');
      setError('');
      setCountdown(getBlockTimeRemaining());
      
      // Auto-focus en el input si no está bloqueado y no es demo
      if (!isBlocked() && !isUserDemo && inputRef.current) {
        setTimeout(() => {
          inputRef.current?.focus();
        }, 100);
      }
    }
    previousOpenRef.current = open;
  }, [open, isBlocked, getBlockTimeRemaining, isUserDemo]);

  // Auto-submit cuando se completan 4 dígitos
  useEffect(() => {
    if (pin.length === 4 && !verifying && !isBlocked()) {
      // MEJORA 4: Pre-validación de formato antes de enviar
      if (/^\d{4}$/.test(pin)) {
        handleVerifyPin();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pin]);

  const handleVerifyPin = async () => {
    if (isBlocked()) {
      return;
    }

    // MEJORA 4: Pre-validación adicional de formato
    if (!pin || pin.length !== 4 || !/^\d{4}$/.test(pin)) {
      setError(t('pinInput.invalidFormat') || 'PIN debe tener exactamente 4 dígitos');
      return;
    }

    setVerifying(true);
    setError('');

    try {
      const response = await fetch('/api/pin/verify', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ 
          pin,
          device_id: deviceId 
        })
      });

      const data = await response.json();

      if (data.success) {
        // PIN correcto
        addPinAttempt(true);
        setPinSession(data.valid_until);
        // CRITICO: Llamar onPinVerified inmediatamente - la sesión ya está en sessionStorage (sync)
        // y el pending control debe ejecutarse ANTES de cualquier onClose que lo limpie.
        onPinVerified();
        // Embebido: no cerrar la vista de cámara. El PIN se oculta en
        // handlePinVerified; la ventana sigue hasta que el usuario toque la X.
        // Standalone: el padre cierra en onPinVerified (no llamar onClose aquí:
        // App.onClose limpiaba pending y anulaba el resume Sonos/Samsung).
      } else {
        // PIN incorrecto
        addPinAttempt(false);
        setError(data.message || t('pinInput.incorrectPin'));
        setPin('');
        
        // Verificar si ahora está bloqueado
        if (isBlocked()) {
          setCountdown(getBlockTimeRemaining());
        }
      }

    } catch (error) {
      console.error('Error verificando PIN:', error);
      setError(t('pinInput.verifyError'));
      setPin('');
    } finally {
      setVerifying(false);
    }
  };

  const handlePinChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // CRITICO: Si el usuario es demo, bloquear la entrada y mostrar snackbar
    if (isUserDemo) {
      if (onShowRestriction) {
        onShowRestriction();
      }
      return; // No permitir escribir
    }
    
    const value = e.target.value;
    // Solo permitir dígitos y máximo 4 caracteres
    if (/^\d{0,4}$/.test(value)) {
      setPin(value);
      setError(''); // Limpiar error al escribir
    }
  };

  const handleClose = () => {
    if (!verifying) {
      setPin('');
      setError('');
      onClose();
    }
  };

  const formatCountdown = (milliseconds: number): string => {
    const totalSeconds = Math.ceil(milliseconds / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  // Memorizar el estado de bloqueo para evitar re-cálculos innecesarios
  const blocked = useMemo(() => isBlocked(), [isBlocked]);

  // Si está embebido, renderizar sin Dialog (solo el contenido) - MÁS COMPACTO, SIN BOTÓN CANCELAR
  if (embedded) {
    return (
      <Box sx={{ width: '100%', backgroundColor: 'transparent' }}>
        {blocked ? (
          <Box sx={{ textAlign: 'center', py: 1 }}>
            <Typography sx={{ color: '#ff6b6b', fontSize: '1rem', mb: 1.5, fontWeight: 'bold' }}>
              {t('pinInput.blocked')}
            </Typography>
            <Typography sx={{ color: '#aaa', fontSize: '0.85rem', mb: 1 }}>
              {t('pinInput.tooManyFailedAttempts')}
            </Typography>
            <Typography sx={{ color: 'white', fontSize: '1.3rem', fontWeight: 'bold' }}>
              {t('pinInput.timeRemaining')} {formatCountdown(countdown)}
            </Typography>
          </Box>
        ) : (
          <Box>
            <TextField
              inputRef={inputRef}
              fullWidth
              label={t('pinInput.pin4Digits')}
              value={pin}
              onChange={handlePinChange}
              type="password"
              disabled={verifying || isUserDemo}
              error={!!error || isUserDemo}
              helperText={isUserDemo ? t('permissions.demoRestriction') : error}
              autoFocus={!isUserDemo}
              size="small"
              inputProps={{
                maxLength: 4,
                inputMode: 'numeric',
                pattern: '[0-9]*',
                style: {
                  fontSize: '1.3rem',
                  textAlign: 'center',
                  letterSpacing: '0.4rem'
                }
              }}
              sx={{
                '& .MuiInputLabel-root': {
                  color: 'rgba(255, 255, 255, 0.7)',
                },
                '& .MuiOutlinedInput-root': {
                  color: 'white',
                  '& fieldset': {
                    borderColor: 'rgba(255, 255, 255, 0.23)',
                  },
                  '&:hover fieldset': {
                    borderColor: 'rgba(255, 255, 255, 0.5)',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#2391FF',
                  },
                  '&.Mui-error fieldset': {
                    borderColor: '#ff6b6b',
                  },
                },
                '& .MuiFormHelperText-root': {
                  color: '#ff6b6b',
                  textAlign: 'center',
                  marginTop: '4px'
                }
              }}
            />

            {/* MEJORA 3: Feedback visual mejorado durante verificación */}
            {verifying && (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mt: 1.5, gap: 1 }}>
                <CircularProgress size={24} sx={{ color: '#2391FF' }} />
                <Typography sx={{ color: '#2391FF', fontSize: '0.85rem', mt: 0.5 }}>
                  {t('pinInput.verifying') || 'Verificando PIN...'}
                </Typography>
              </Box>
            )}
            {/* NO mostrar botón cancelar cuando está embebido - el usuario puede cerrar con la X del video */}
          </Box>
        )}
      </Box>
    );
  }

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      PaperProps={{
        style: {
          backgroundColor: '#1E1E1E',
          color: 'white',
          minWidth: '350px',
          maxWidth: '400px'
        }
      }}
      sx={{
        '& .MuiDialog-paper': {
          margin: '16px'
        }
      }}
    >
      <DialogContent sx={{ paddingTop: '16px !important', paddingBottom: embedded ? '8px' : '8px' }}>
        {blocked ? (
          // Vista de bloqueo
          <Box sx={{ textAlign: 'center', py: 1 }}>
            <Typography sx={{ color: '#ff6b6b', fontSize: '1rem', mb: 1.5, fontWeight: 'bold' }}>
              {t('pinInput.blocked')}
            </Typography>
            <Typography sx={{ color: '#aaa', fontSize: '0.85rem', mb: 1 }}>
              {t('pinInput.tooManyFailedAttempts')}
            </Typography>
            <Typography sx={{ color: 'white', fontSize: '1.3rem', fontWeight: 'bold' }}>
              {t('pinInput.timeRemaining')} {formatCountdown(countdown)}
            </Typography>
          </Box>
        ) : (
          // Vista normal
          <Box>
            <Typography sx={{ color: '#aaa', fontSize: '0.85rem', mb: embedded ? 1 : 1.5 }}>
              {t('pinInput.requiredMessage')}
            </Typography>

            <TextField
              inputRef={inputRef}
              fullWidth
              label={t('pinInput.pin4Digits')}
              value={pin}
              onChange={handlePinChange}
              type="password"
              disabled={verifying || isUserDemo}
              error={!!error || isUserDemo}
              helperText={isUserDemo ? t('permissions.demoRestriction') : error}
              autoFocus={!isUserDemo}
              size="small"
              inputProps={{
                maxLength: 4,
                inputMode: 'numeric',
                pattern: '[0-9]*',
                style: {
                  fontSize: '1.3rem',
                  textAlign: 'center',
                  letterSpacing: '0.4rem'
                }
              }}
              sx={{
                '& .MuiInputLabel-root': {
                  color: 'rgba(255, 255, 255, 0.7)',
                },
                '& .MuiOutlinedInput-root': {
                  color: 'white',
                  '& fieldset': {
                    borderColor: 'rgba(255, 255, 255, 0.23)',
                  },
                  '&:hover fieldset': {
                    borderColor: 'rgba(255, 255, 255, 0.5)',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#2391FF',
                  },
                  '&.Mui-error fieldset': {
                    borderColor: '#ff6b6b',
                  },
                },
                '& .MuiFormHelperText-root': {
                  color: '#ff6b6b',
                  textAlign: 'center',
                  marginTop: '4px'
                }
              }}
            />

            {/* MEJORA 3: Feedback visual mejorado durante verificación */}
            {verifying && (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mt: embedded ? 1 : 1.5, gap: 1 }}>
                <CircularProgress size={24} sx={{ color: '#2391FF' }} />
                <Typography sx={{ color: '#2391FF', fontSize: '0.85rem', mt: 0.5 }}>
                  {t('pinInput.verifying') || 'Verificando PIN...'}
                </Typography>
              </Box>
            )}
          </Box>
        )}
      </DialogContent>

      {/* Solo mostrar botón cancelar si NO está embebido */}
      {!embedded && (
        <DialogActions sx={{ padding: '8px 16px 12px' }}>
          <Button
            onClick={handleClose}
            disabled={verifying}
            sx={{
              color: 'white',
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.1)'
              }
            }}
          >
            {t('common.cancel')}
          </Button>
        </DialogActions>
      )}
    </Dialog>
  );
};

export default PinInputDialog;

